// @flow

/**
 * Utilities that deal with currency, leveraging client-side Griffin 🎉
 */

import griffinNumber from 'griffin-paypal/number'
import { parseLocalizedNumberAsFloat } from '../../lib/amountUtils'
import getGriffinMetadata from './griffin-utils'
import log from 'loglevel'
import { isXoomCurrency } from './currencyConversionUtils'
import content from 'pp-react-l10n'
import { PRIMARY } from '../constants.js'
import { fixP2PAFCurrencySymbolNotDisplayed } from '../../experiments/showCurrencySymbolFixGriffinIssue'

const i18n = content('crossBorder/currencies')

/**
 * Get the supported currency pattern for specific style for the current locale
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} griffinPresetKey  griffin preset keys for various
 * s of output
 * @param  {number} maximumFractionDigits If we want to set the maximum fraction digits to zero or something else
 * @return {String}               the formatted amount
 */
export function formatAmount(
  amount: string,
  currencyCode: string,
  griffinPresetKey: string = 'CURRENCY_FORMAT_SYMBOLISOCURRENCY',
  maximumFractionDigits: ?number
) {
  if (!amount || !currencyCode) {
    log.warn('formatAmount() requires amount and currencyCode')
    return ''
  }

  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)
  const normalizedAmount = griffinNumberHandler.normalizeNumber(amount)
  const options = griffinNumberHandler[griffinPresetKey]
  if (maximumFractionDigits !== undefined) {
    options.maximumFractionDigits = maximumFractionDigits
  }
  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: normalizedAmount, // this argument only accepts floats or strings in US number format
    },
    options
  )
}

/**
 * Get the supported currency pattern for specific style for the current locale
 * without normalizing the data as canonical data is already normalized.
 * Use this method when you are getting the value from our services already
 * in cannonical form regardless of the currency or country.
 * Example: normalized (canonical): 50.00 EUR
 *          unnormalized: 50,00 EUR (requires normalizing before formatting)
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} griffinPresetKey  griffin preset keys for various formats of output
 * @return {String}               the formatted amount
 * @throws {Object}               TypeError from griffin
 */
export function formatCanonicalAmount({
  amount,
  currencyCode,
  griffinPresetKey = 'CURRENCY_FORMAT_SYMBOLISOCURRENCY',
}: {
  amount: string,
  currencyCode: string,
  griffinPresetKey: string,
}) {
  if (!amount || !currencyCode) {
    log.warn('formatCanonicalAmount() requires amount and currencyCode')
    return ''
  }

  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)
  // @throws TypeError
  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: amount, // this argument only accepts floats or strings in US number format
    },
    griffinNumberHandler[griffinPresetKey]
  )
}

/**
 * Get the amount in an ordered array of "parts" that you can assemble together
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} separator      decimal separator
 * @return {Array}                the amount parts
 */
export function getAmountParts(
  amount: string,
  currencyCode: string,
  separator: CurrencySeparator
) {
  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)

  const amountAsFloat = parseLocalizedNumberAsFloat(amount, separator)

  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: amountAsFloat,
    },
    griffinNumberHandler.CURRENCY_FORMAT_SYMBOLISOCURRENCY.parts()
  )
}

/**
 * Get sorted currency code list
 * @param  {Array<string>} currencyCodeList   the currency code list
 * @param  {Object} currencySymbolMap         the map with currency code and symbol
 * @return {Array<string>}                    the sorted currency code list
 */
export function sortCurrencyList(
  currencyCodeList: Array<string>,
  currencySymbolMap: Object
): Array<string> {
  let currencyList = []
  if (currencyCodeList && currencyCodeList.length) {
    currencyList = currencyCodeList
  } else {
    currencyList = Object.keys(currencySymbolMap)
  }

  return currencyList.sort()
}

export function showCurrencySymbol(
  amount: string,
  currencyCode: string,
  currencyDecimal: CurrencySeparator,
  currencyMap: Object
) {
  if (fixP2PAFCurrencySymbolNotDisplayed()) {
    return currencyMap ? !!currencyMap[currencyCode] : false
  }
  const parts = getAmountParts(amount, currencyCode, currencyDecimal)

  return parts.find(part => part.type === 'symbol') || false
}

/**
 * Given a list of currency codes, get their names for the current locale
 * @param  {Array<string>} currencyCodeList currencies to localize
 * @return {Array<string>} list of currency names
 */
export function getCurrencyNames(
  currencyCodeList: Array<string>
): Array<string> {
  return currencyCodeList.map((currencyCode: string): string =>
    i18n(`currencies.${currencyCode}.displayName`)
  )
}

/*
 * Order payment destinations so the primary is the first one
 */
export function getDestinationsPrimaryFirst(
  availablePaymentDestinations: Array<Object>
): Array<Object> {
  const primaryDestinations = availablePaymentDestinations.filter(
    item => item.tags?.[0] === PRIMARY
  )
  const nonPrimaryDestinations = availablePaymentDestinations.filter(
    item => item.tags?.[0] !== PRIMARY
  )

  // Place primary items at the top, followed by the rest
  return [...primaryDestinations, ...nonPrimaryDestinations]
}

/*
 * Get a list of currencies from a list of payment destinations
 */
export function getCurrenciesFromPaymentDestinations(
  availablePaymentDestinations: Array<Object>
): Array<string> {
  const sortedPaymentDestinations = getDestinationsPrimaryFirst(
    availablePaymentDestinations
  )
  return sortedPaymentDestinations.map(destination => destination.currencyCode)
}

/**
 * Given a list of currency codes, return a new list with these codes
 * mapped within an object under the key 'currencyCode'
 * @param  {Array<string>} currencyCodeList currency codes to map
 * @return {Array<Object>} list of objects with 'currencyCode' key
 */
export function mapCurrencyCodeList(
  currencyCodeList: Array<string>
): Array<Object> {
  return currencyCodeList.map(currencyCode => ({ currencyCode }))
}

export function getPrimaryDestinationCurrency(
  availablePaymentDestinations: Array<Object>
) {
  const foundCurrency = availablePaymentDestinations.find(currency =>
    currency.tags.includes(PRIMARY)
  )

  return foundCurrency?.currencyCode || ''
}

export function getPaymentIdByCurrencyCode(
  availablePaymentDestinations: Array<Object>,
  currencyCode: string
) {
  const foundItem = availablePaymentDestinations.find(
    item => item.currencyCode === currencyCode
  )

  return foundItem?.id
}

export function isCrossCurrencyEnabled(options: Object = {}) {
  let isCrossBorder = false
  if (
    !isXoomCurrency(options.amount.currencyCode) &&
    !isXoomCurrency(options.toCurrencyCode) &&
    options.fromCurrencyCode !== options.toCurrencyCode
  ) {
    isCrossBorder = true
  }

  return isCrossBorder
}
